import {
	IJarvisUser,
	Id,
	LaravelResponse,
	RoleIndexResource,
	RoleOptionResource,
	keyToUrl,
	withDestroyEndpoint,
	withIndexEndpoint,
	withOptionsEndpoint,
	withShowEndpoint,
	withStoreEndpoint,
	withUpdateEndpoint,
} from "@dgs/core";
import axios from "axios";
import { TenantIndexResource, TenantOptionResource } from "../../../../shared/types/tenant";
import { UserIndexResource } from "../types";
import {
	IApplicationUserRequest,
	UserCreateRequest,
	UserOptionsResource,
	UserSaveRequest,
	UserUpdateResource,
} from "../types/user";
import { UserGroupOptionResource } from "../types/userGroup";

const userServiceBaseKey = "users";

export const userService = {
	optionsWithDevices: (search: string, page: number, params?: Record<string, any>) =>
		axios.get<LaravelResponse<UserOptionsResource[]>>(keyToUrl(["users", "options", "with-devices"]), {
			params: { ...params, search, page },
		}),
	optionsWithDevicesKey: [userServiceBaseKey, "options", "optionsWithDevices"],
	block: (userId: Id) => axios.put(keyToUrl([userServiceBaseKey, `${userId}`, "block"])),
	unblock: (userId: Id) => axios.put(keyToUrl([userServiceBaseKey, `${userId}`, "unblock"])),

	...withIndexEndpoint<UserIndexResource>([userServiceBaseKey]),
	...withShowEndpoint<UserUpdateResource>([userServiceBaseKey]),
	...withStoreEndpoint<UserCreateRequest>([userServiceBaseKey]),
	...withUpdateEndpoint<UserSaveRequest>([userServiceBaseKey]),
	...withOptionsEndpoint<UserOptionsResource>([userServiceBaseKey]),
	...withDestroyEndpoint([userServiceBaseKey]),
};

const baseEndPoints = (baseKey: string[]) => {
	const getAvailableUserGroupsKey = [...baseKey, "available-group-options"];
	const getAvailableUsersKey = [...baseKey, "available-options"];
	return {
		getAvailableUserGroups: (search: string, page: number, params?: Record<string, any>) =>
			axios.get<LaravelResponse<UserGroupOptionResource[]>>(keyToUrl(getAvailableUserGroupsKey), {
				params: { ...params, search, page },
			}),
		getAvailableUserGroupsKey,
		getAvailableUsers: (search: string, page: number, params?: Record<string, any>) =>
			axios.get<LaravelResponse<UserOptionsResource[]>>(keyToUrl(getAvailableUsersKey), {
				params: { ...params, search, page },
			}),
		getAvailableUsersKey,
	};
};

export const createApplicationUserService = (id: Id) => {
	const baseKey = ["applications", `${id}`, "users"];
	return {
		attachUsers: (values: IApplicationUserRequest) =>
			axios.post<LaravelResponse<void>>(`${keyToUrl([...baseKey, "attach"])}`, values),
		detachUsers: (userIds: Id[]) =>
			axios.post<LaravelResponse<void>>(`${keyToUrl([...baseKey, "detach"])}`, {
				userIds,
			}),
		...withIndexEndpoint<IJarvisUser>(baseKey),
		...baseEndPoints(baseKey),
	};
};

export const createUserRolesService = (id: Id) => {
	const baseKey = ["users", `${id}`, "roles"];
	const roleOptionsByUserKey = [...baseKey, "options"];
	return {
		attachRoles: (roleIds: Id[]) => axios.post(`${keyToUrl([...baseKey, "attach"])}`, { roleIds }),
		detachRoles: (roleIds: Id[]) => axios.post(`${keyToUrl([...baseKey, "detach"])}`, { roleIds }),
		...withIndexEndpoint<RoleIndexResource>(baseKey),
		roleOptionsByUser: (search: string, page: number, params?: Record<string, any>) => {
			return axios.get<LaravelResponse<RoleOptionResource[]>>(keyToUrl(roleOptionsByUserKey), {
				params: { ...params, search, page },
			});
		},
		roleOptionsByUserKey,
	};
};

export const createUserTenantsService = (id: Id) => {
	const baseKey = ["users", `${id}`, "tenants"];
	const tenantOptionsByUserKey = [...baseKey, "options"];
	return {
		attachTenants: (tenantIds: Id[]) => axios.post(`${keyToUrl([...baseKey, "attach"])}`, { tenantIds }),
		detachTenants: (tenantIds: Id[]) => axios.post(`${keyToUrl([...baseKey, "detach"])}`, { tenantIds }),
		...withIndexEndpoint<TenantIndexResource>(baseKey),
		tenantOptionsByUser: (search: string, page: number, params?: Record<string, any>) => {
			return axios.get<LaravelResponse<TenantOptionResource[]>>(keyToUrl(tenantOptionsByUserKey), {
				params: { ...params, search, page },
			});
		},
		tenantOptionsByUserKey,
	};
};
